import { generatePath } from 'react-router-dom'
import type { PopoverOrigin } from '@mui/material/Popover'
import { TASK_CATEGORY_FILTER_FIELD } from '@common/constants/filters'
import { ActivityType } from '@common/interfaces/activity'
import { ARC_AUTOMATION_ID_QUERY_PARAM } from '@common/interfaces/atlasai'
import type { IFile } from '@common/interfaces/box'
import { customFieldKey } from '@common/interfaces/customField'
import { BOARD_AMENDMENT_STATUSES } from '@common/interfaces/generalAnalysis'
import {
  ITask,
  ITaskLinkedFile,
  TaskSource,
  TaskStatus,
  TaskStatusValue,
  hasOpenLinkedRecord,
  isLinkedTask,
} from '@common/interfaces/task'
import { ITaskProject, TaskProjectRole } from '@common/interfaces/taskProject'
import { IUser, UserRole } from '@common/interfaces/user'
import moment, { MomentInput } from 'moment'
import type { IOption } from '../../components/Common/SelectField'
import { companyProfileHref, orgHref } from '../../components/Crm/crmLinks'
import { ROUTES } from '../../constants/routes'
import { getCollectionsActivityDeepLink } from '../../helpers/getCollectionsActivityDeepLink'
import {
  daysFromToday,
  formatRelativeDueDate,
  relativeDueDateTone,
} from '../../helpers/relativeDueDate'
import { getReportingDocumentRoute } from '../../helpers/reportingRoutes'
import { AttachmentUploadStatus } from '../../hooks/useFileAttachments'
import type { IInlineTaskCreatePayload } from './components/InlineTaskCreation'

/** Page size for the task list views (My Tasks + project list) — shared so the
 *  two paginate consistently. */
export const TASKS_LIST_PER_PAGE = 80

/** Page size for the client page's Tasks tab. Much smaller than the full-page
 *  lists above: the sidebar shows ~10-15 rows at a time and each row carries
 *  joined assignee/activity/reporting data, so it pages in as you scroll
 *  instead of pulling the client's whole backlog up front. */
export const CLIENT_TASKS_PER_PAGE = 25

/** Ad-hoc = no live upstream record. A linked task's status, company and category
 *  mirror that record; everything else is the user's to change. */
export const isAdHocTask = (task: Parameters<typeof isLinkedTask>[0]): boolean =>
  !isLinkedTask(task)

const LINKED_RECORD_LABELS: Partial<Record<TaskSource, string>> = {
  [TaskSource.Funding]: 'Funding',
  [TaskSource.Collections]: 'Collection',
  [TaskSource.Reporting]: 'Report',
  [TaskSource.FlagsInvestigation]: 'Flag',
  [TaskSource.BDDocuments]: 'BD Documents',
  [TaskSource.DDDocuments]: 'DD Documents',
  [TaskSource.Amendments]: 'Amendment',
}

/** What a linked task's record is called in copy — "Funding", "Amendment". */
export const getLinkedRecordLabel = (task: Pick<ITask, 'source'>): string =>
  LINKED_RECORD_LABELS[task.source] ?? 'linked record'

/** The linked record's own status, which `status` stops mirroring once the user
 *  completes the task. */
export const getWorkflowStatus = (task: Pick<ITask, 'status' | 'workflowStatus'>) =>
  task.workflowStatus ?? task.status

/** The statuses a board may set on an amendment task, plus the task's current
 *  value when it is not one of them (Complete, which mirrors Executed) so
 *  the select can still display it — disabled, since re-picking it does nothing. */
export const getAmendmentTaskStatusOptions = (
  currentStatus: TaskStatusValue | null,
): IOption<TaskStatusValue>[] => {
  const options: IOption<TaskStatusValue>[] = BOARD_AMENDMENT_STATUSES.map((status) => ({
    value: status,
    label: status,
  }))
  if (currentStatus && !options.some((option) => option.value === currentStatus)) {
    options.unshift({ value: currentStatus, label: currentStatus, disabled: true })
  }
  return options
}

/** A task reads as complete in the UI. One definition so the row, the detail
 *  panel, and a rendered task-link chip can't disagree. Deliberately narrower
 *  than `CLOSED_TASK_STATUSES` (which also covers Archived) — archived tasks are
 *  filtered out of the list endpoints, and every UI surface has always treated
 *  only `Complete` as the green-check state. */
export const isTaskComplete = (task: Pick<ITask, 'status'>): boolean =>
  task.status === TaskStatus.Complete

/**
 * Whether the viewer may stop the Arc run on a task. `canStopArc` is annotated
 * per read, so it is absent on a task whose flag flipped live over
 * ATLAS_AI_TASK_WORKING — the ids that event carries are what keep the Stop
 * control reachable until the next fetch. Same rule on both sides (assignee or
 * collaborator), and `tasks/CancelArcRun` re-checks it server-side.
 */
export const canStopArcRun = (task: ITask, currentUserId?: string): boolean =>
  Boolean(task.canStopArc) ||
  Boolean(currentUserId && task.arcStoppableByUserIds?.includes(currentUserId))

/** Client mirror of the server's `assertProjectAdmin`: an Atlas admin manages every
 *  project, member of it or not. Gates the admin-only actions, not their visibility. */
export const canManageProject = (
  userRole: UserRole | null | undefined,
  project: Pick<ITaskProject, 'currentUserRole'> | null | undefined,
): boolean => userRole === UserRole.ADMIN || project?.currentUserRole === TaskProjectRole.Admin

/**
 * Returns the deep-link path that the "Open / View" button should navigate to
 * for this task, or null if there's no upstream surface to open. One central
 * predicate so the button / tooltip link / category-cell link all gate on
 * the same condition (replacing the older per-surface `isStructured` /
 * `hasUpstreamActivity` / `!isAdHocTask` checks).
 */
export const getTaskNavigationLink = (task: ITask): string | null => {
  if (task.ongoingReportingId) {
    const reportType = task.ongoingReporting?.type
    return reportType
      ? getReportingDocumentRoute(reportType, task.ongoingReportingId, null)
      : ROUTES.ONGOING_REPORTING.replace(':id', task.ongoingReportingId)
  }

  if (task.activity) {
    const collectionsPath = getCollectionsActivityDeepLink(task.activity)
    if (collectionsPath) {
      return collectionsPath
    }
    const activityType = task.activity.type
    const bbcId = task.activity.borrowingBaseId ?? task.activity.borrowingBase?.id
    if (bbcId && [ActivityType.Funding, ActivityType.TestBBC].includes(activityType)) {
      return generatePath(ROUTES.BBC_SUMMARY, { id: bbcId })
    }
  }

  if (task.activityId) {
    return ROUTES.ACTIVITY_QUEUE
  }

  if (task.flagId) {
    // The Flags page only shows Active flags, so deep-linking to a resolved
    // flag silently no-ops. Hide the button once the task is closed — the
    // flag is gone and there's nothing actionable to view.
    if (task.status === TaskStatus.Complete || task.status === TaskStatus.Archived) {
      return null
    }
    // The Flags tab's canonical route, not `/`. This used to point at `/` and
    // rely on it redirecting internal roles to client-management with the query
    // string intact; `/` now renders the dashboard, so a root-relative link
    // would land back on the dashboard and silently do nothing.
    return `${ROUTES.CLIENT_MANAGEMENT}?tab=Flags&flagId=${task.flagId}`
  }

  if (task.source === TaskSource.BDDocuments && task.opsReportingId) {
    return `/prospects/${task.opsReportingId}/analysis-queue`
  }

  // The Validate documents tab, not the bare route (which renders the Overview):
  // a DD Documents task is kept open by requests sitting at InReview, and that
  // tab is where they are listed and moved off it.
  if (task.source === TaskSource.DDDocuments && task.clientInfo?.id) {
    return generatePath(ROUTES.DUE_DILIGENCE_VALIDATE_DOCUMENTS_PAGE, {
      id: task.clientInfo.id,
    })
  }

  if (task.source === TaskSource.CommitteeSubmission && task.opsReportingId) {
    return `/prospects/${task.opsReportingId}/summary`
  }

  if (task.source === TaskSource.TermsApproval && task.opsReportingId) {
    return generatePath(ROUTES.PROSPECT_MANAGE_TERMS_PAGE, { id: task.opsReportingId })
  }

  if (task.source === TaskSource.Amendments && task.generalAnalysisId && task.clientInfo?.id) {
    return generatePath(ROUTES.AMENDMENT_ANALYSIS, {
      id: task.clientInfo.id,
      analysisId: task.generalAnalysisId,
    })
  }

  return null
}

/**
 * Source-aware label for the navigation button / tooltip. Returns null when
 * `getTaskNavigationLink` would also return null (caller should not render
 * the button at all in that case).
 */
export const getTaskNavigationLabel = (task: ITask): string | null => {
  // Names the work, not the page: one Funding task lands on BBC Summary or the
  // Activity Queue depending on its activity, and Collections splits three ways.
  // Keying off `source` keeps this agreeing with the Source column beside it.
  if (task.ongoingReportingId) {
    return 'Open Reporting'
  }
  if (task.activityId) {
    return task.source === TaskSource.Collections ? 'Open Collections' : 'Open Funding'
  }
  if (task.flagId) {
    if (task.status === TaskStatus.Complete || task.status === TaskStatus.Archived) {
      return null
    }
    return 'View Flag'
  }
  if (task.source === TaskSource.BDDocuments && task.opsReportingId) {
    return 'View Documents'
  }
  if (task.source === TaskSource.DDDocuments && task.clientInfo?.id) {
    return 'View Documents'
  }
  if (task.source === TaskSource.CommitteeSubmission && task.opsReportingId) {
    return 'View Submission'
  }
  if (task.source === TaskSource.TermsApproval && task.opsReportingId) {
    // Not a document viewer: this opens Manage Terms, where the committee
    // reviews and approves the submitted structure. "View Term Sheet" described
    // the wrong thing. Distinct from the CommitteeSubmission task above, which
    // keeps "View Submission" and routes to the Deal Summary.
    return 'Committee Review'
  }
  if (task.source === TaskSource.Amendments && task.generalAnalysisId && task.clientInfo?.id) {
    return 'View Amendment'
  }
  return null
}

/**
 * Where a task's source indicator leads. Several outcomes rather than a path,
 * because the sources genuinely differ: workflow tasks navigate in-app, a Call
 * task leaves for the Fireflies recording, a Feedback task opens its submission
 * in place, and a task Arc was asked for in chat has nothing to open — only
 * something to say. Null means render the source as
 * plain text (Manual, and any task whose origin was never recorded).
 */
type TaskSourceTarget =
  | { kind: 'internal'; path: string; label: string }
  | { kind: 'external'; url: string; label: string }
  | { kind: 'tooltip'; label: string }
  | { kind: 'feedback'; feedbackId: string; label: string }
  | null

/** Deep-link to a single automation on the Automations page. Shared by the source
 *  indicator and the detail panel's created-by attribution. */
export const getAutomationPath = (automationId: string): string =>
  `${ROUTES.ATLAS_AI_AUTOMATIONS}?${ARC_AUTOMATION_ID_QUERY_PARAM}=${automationId}`

export const getTaskSourceTarget = (task: ITask): TaskSourceTarget => {
  if (task.source === TaskSource.Manual) {
    return null
  }

  if (task.source === TaskSource.Feedback) {
    const origin = task.feedbackOrigin
    if (!origin || !task.feedbackSubmissionId) {
      return null
    }
    const label = `Submitted via Feedback by ${origin.submitterName ?? 'Unknown user'} on ${moment(
      origin.submittedAt,
    ).format('MMM D, YYYY h:mm A')}`
    if (!origin.canOpen) {
      return { kind: 'tooltip', label }
    }
    return { kind: 'feedback', feedbackId: task.feedbackSubmissionId, label }
  }

  if (task.source === TaskSource.Call) {
    const url = task.firefliesTranscript?.transcriptUrl
    if (!url) {
      return null
    }
    // The cited sentence's offset, byte-identical to the `?t=` the note source
    // chips (Note.tsx) and the major-developments digest already build — raw
    // seconds, not rounded. Absent when the creating path cited no sentence,
    // which opens the transcript at the top instead.
    const at = task.callStartTime != null ? `?t=${task.callStartTime}` : ''
    return { kind: 'external', url: `${url}${at}`, label: 'Open call transcript' }
  }

  // Arc and automation tasks share one source value, so which of the two this
  // is comes from the resolved origin rather than from `source`.
  if (task.source === TaskSource.Arc) {
    const origin = task.arcOrigin
    if (!origin) {
      return null
    }
    if (origin.kind === 'automation') {
      const label = origin.automationOwnerName
        ? `Created via ${origin.automationName} by ${origin.automationOwnerName}`
        : `Created via ${origin.automationName}`
      // Same wording either way; only whether it links differs. Not linked for a
      // viewer who can't open it — the Automations page would 404 into a blank
      // create form (see resolveTaskOrigins).
      if (!origin.canOpen) {
        return { kind: 'tooltip', label }
      }
      return { kind: 'internal', path: getAutomationPath(origin.automationId), label }
    }
    return {
      kind: 'tooltip',
      label: `Created via Arc Chat by ${origin.userName ?? 'Arc'} at ${moment(
        task.createdAt,
      ).format('MMM D, YYYY h:mm A')}`,
    }
  }

  const path = getTaskNavigationLink(task)
  if (!path) {
    return null
  }
  return { kind: 'internal', path, label: getTaskNavigationLabel(task) ?? 'Open activity' }
}

/** Open subtask count for the parent-completion warning. List rows carry the
 *  precomputed `incompleteSubtaskCount` (rows are lazy-loaded); the panel task
 *  (from Show) has no count but eager-loads `subtasks`, so derive from those. */
export const countIncompleteSubtasks = (task: ITask): number => {
  if (typeof task.incompleteSubtaskCount === 'number') {
    return task.incompleteSubtaskCount
  }
  return (task.subtasks ?? []).filter(
    (subtask) => subtask.status !== TaskStatus.Complete && subtask.status !== TaskStatus.Archived,
  ).length
}

/** What confirming the completion warning adds to the write: the open subtasks it
 *  names are completed with their tasks. */
export interface ICompleteOptions {
  completeOpenSubtasks?: boolean
}

export type CompleteProceed = (options: ICompleteOptions) => void | Promise<unknown>

/** The options a confirmed warning for these tasks sends. */
export const completeOptionsFor = (tasks: ITask[]): ICompleteOptions =>
  tasks.some((task) => countIncompleteSubtasks(task) > 0) ? { completeOpenSubtasks: true } : {}

/** Why completing this task deserves a confirm, or null when it doesn't. */
export const singleCompleteWarning = (task: ITask): string | null => {
  const warnings: string[] = []
  const count = countIncompleteSubtasks(task)
  if (count > 0) {
    warnings.push(
      `This task has ${count} incomplete subtask${count === 1 ? '' : 's'}. ${
        count === 1 ? 'It' : 'They'
      } will be completed too.`,
    )
  }
  if (hasOpenLinkedRecord(task)) {
    warnings.push(
      `The ${getLinkedRecordLabel(task)} is still ${getWorkflowStatus(task)}. Completing this task won't change it.`,
    )
  }
  return warnings.length > 0 ? warnings.join(' ') : null
}

/** The aggregate version of `singleCompleteWarning` for a bulk complete. */
export const bulkCompleteWarning = (tasks: ITask[]): string | null => {
  const warnings: string[] = []
  const withSubtasks = tasks.filter((task) => countIncompleteSubtasks(task) > 0).length
  if (withSubtasks > 0) {
    warnings.push(
      `${withSubtasks} selected ${
        withSubtasks === 1 ? 'task has' : 'tasks have'
      } incomplete subtasks that will be completed too.`,
    )
  }
  const withOpenRecords = tasks.filter(hasOpenLinkedRecord).length
  if (withOpenRecords > 0) {
    warnings.push(
      `${withOpenRecords} selected ${
        withOpenRecords === 1
          ? 'task has an unfinished linked record'
          : 'tasks have unfinished linked records'
      }. Completing won't change ${withOpenRecords === 1 ? 'it' : 'them'}.`,
    )
  }
  return warnings.length > 0 ? warnings.join(' ') : null
}

/** Total subtask count for the delete-confirmation warning. Same list-vs-panel
 *  split as countIncompleteSubtasks: list rows carry `subtaskCount`, the panel
 *  task (from Show) eager-loads `subtasks` instead. */
export const countSubtasks = (task: ITask): number => {
  if (typeof task.subtaskCount === 'number') {
    return task.subtaskCount
  }
  return (task.subtasks ?? []).length
}

export const deleteTaskWarning = (task: ITask): string => {
  const subtaskCount = countSubtasks(task)
  const boardCount = task.placements?.length ?? 0
  const parts = ['Deleting this task will be permanent.']
  if (isLinkedTask(task)) {
    parts.push(`This removes the task only. The ${getLinkedRecordLabel(task)} is not affected.`)
  }
  if (subtaskCount > 0) {
    parts.push(`It will also delete its ${subtaskCount} subtask${subtaskCount === 1 ? '' : 's'}.`)
  }
  if (boardCount > 1) {
    parts.push(`This task is on ${boardCount} boards — deleting it removes it from all of them.`)
  }
  return parts.join(' ')
}

const isOpenWithDueDate = (task: ITask): boolean =>
  Boolean(task.dueDate) && task.status !== TaskStatus.Complete

/** A task is overdue when its due date is before today and it isn't complete. */
export const isOverdue = (task: ITask): boolean =>
  isOpenWithDueDate(task) && relativeDueDateTone(task.dueDate) === 'overdue'

/** Due today and not complete — mutually exclusive with `isOverdue`. */
export const isDueToday = (task: ITask): boolean =>
  isOpenWithDueDate(task) && daysFromToday(task.dueDate) === 0

/** Due tomorrow and not complete — shares "Tomorrow" copy and green treatment with today. */
export const isDueTomorrow = (task: ITask): boolean =>
  isOpenWithDueDate(task) && daysFromToday(task.dueDate) === 1

/** Today or tomorrow, Asana's two immediate buckets that get the green treatment. */
export const isDueTodayOrTomorrow = (task: ITask): boolean =>
  isOpenWithDueDate(task) && relativeDueDateTone(task.dueDate) === 'soon'

/** Asana-style relative label shared by every task date: Due, Created, Completed and custom. */
export const formatTaskDateLabel = (date: MomentInput): string => formatRelativeDueDate(date)

/**
 * Relative label for a task's due date; assumes `task.dueDate` is set. It reads the same once
 * the task is complete: only the tone (see `isDueTodayOrTomorrow`) depends on status.
 */
export const formatTaskDueDateLabel = (task: ITask): string => formatTaskDateLabel(task.dueDate)

/**
 * Overdue first (most overdue first), then soonest due date, then undated
 * tasks by most recently created. Shared by every card-style task list
 * (Overview's TasksCard, the client page's Tasks tab) so they sort identically.
 */
export const compareTasksByOverdue = (a: ITask, b: ITask): number => {
  const aOverdue = isOverdue(a)
  const bOverdue = isOverdue(b)
  if (aOverdue !== bOverdue) return aOverdue ? -1 : 1

  if (a.dueDate && b.dueDate) {
    return moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf()
  }
  if (a.dueDate && !b.dueDate) return -1
  if (!a.dueDate && b.dueDate) return 1

  return moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
}

/**
 * The deep-link for a task's Company link. A linked company routes to its profile
 * at its current stage (client page / diligence / prospect / CRM company page);
 * otherwise it falls back to the task → clientInfo path through any of its source
 * records (direct, activity, ongoing reporting), which is how structured tasks —
 * which carry no company — still reach their client page. Null when neither
 * resolves.
 */
export const getTaskCompanyPath = (task: ITask): string | null => {
  if (task.company) {
    return companyProfileHref(task.company)
  }
  const id =
    task.clientInfo?.id || task.activity?.clientInfo?.id || task.ongoingReporting?.clientInfo?.id
  return id ? generatePath(ROUTES.CLIENT_PAGE, { id }) : null
}

/** The task's company label — the linked company, else its client name. */
export const getTaskCompanyName = (task: ITask): string | null =>
  task.company?.name || task.clientName || null

/**
 * What the Company / Org column shows and opens: the task's own company, else the
 * server-resolved fallback (its organization, or its person's primary company or
 * organization). Null when there is none. `path` is null for a name with no page.
 */
export const getTaskCompanyOrOrgLink = (
  task: ITask,
): { name: string; path: string | null } | null => {
  const companyName = getTaskCompanyName(task)
  if (companyName) {
    return { name: companyName, path: getTaskCompanyPath(task) }
  }
  const { affiliation } = task
  if (!affiliation) {
    return null
  }
  const path = affiliation.company
    ? companyProfileHref(affiliation.company)
    : affiliation.organizationId
      ? orgHref(affiliation.organizationId)
      : null
  return { name: affiliation.name, path }
}

/**
 * Flatten the files attached to a task across its source records (activity,
 * activity's borrowing base, ongoing reporting). Dedupes by file id since the
 * same file can appear under more than one source.
 */
export const collectTaskLinkedFiles = (
  task: ITask,
): { id: string; name: string; link: string; fileId?: string }[] => {
  const map = new Map<string, { id: string; name: string; link: string; fileId?: string }>()
  const add = (f: ITaskLinkedFile | IFile) => {
    const name = f.fileName || f.name || 'File'
    const link = f.boxLink || f.link || ''
    map.set(f.id, { id: f.id, name, link, fileId: f.fileId })
  }
  task.activity?.files?.forEach(add)
  task.activity?.borrowingBase?.files?.forEach(add)
  task.ongoingReporting?.files?.forEach(add)
  return [...map.values()]
}

// A parked completion awaiting the non-blocking incomplete-subtasks warning.
export interface ICompleteConfirm {
  message: string
  proceed: CompleteProceed
  // Sent to `proceed` on confirm.
  options?: ICompleteOptions
  // Default to the completion wording; bulk Archive passes its own.
  title?: string
  confirmText?: string
}

// Clicks landing inside these overlays must NOT cancel an open inline-create row
// (the row's outside-click/keydown handler ignores them). The task detail drawer
// is page-specific, so callers prepend their own `.taskDrawerPaper` class.
export const INLINE_CREATE_OVERLAY_SELECTORS = [
  '.MuiDialog-root',
  '.MuiPopover-root',
  '.MuiMenu-root',
  '.MuiAutocomplete-popper',
  '.MuiAutocomplete-paper',
  '[role="listbox"]',
  '[role="menu"]',
  '.MuiPickersDay-root',
  '.MuiDateCalendar-root',
  '.MuiPickersLayout-root',
]

/** The create request for an inline-create draft. Create takes columns only, so
 *  the drafted custom-field values go through `saveDraftCustomFieldValues` after. */
export const toCreateTaskRequest = (draft: IInlineTaskCreatePayload) => ({
  title: draft.title,
  status: draft.status || TaskStatus.NotStarted,
  ...(draft.dueDate ? { dueDate: draft.dueDate } : {}),
  ...(draft.companyId ? { companyId: draft.companyId } : {}),
  ...(draft.assignedToUserId !== undefined ? { assignedToUserId: draft.assignedToUserId } : {}),
})

export const saveDraftCustomFieldValues = async (
  taskId: string,
  customFieldValues: IInlineTaskCreatePayload['customFieldValues'],
  setTaskCustomFieldValue: (taskId: string, data: object) => unknown,
): Promise<void> => {
  await Promise.all(
    Object.entries(customFieldValues ?? {}).map(([customFieldDefinitionId, value]) =>
      setTaskCustomFieldValue(taskId, { customFieldDefinitionId, ...value }),
    ),
  )
}

/**
 * Category's filter/sort travel differently from the other columns: it's a
 * custom field, so the request carries a structured `customFieldFilters` entry
 * and a `cf_<uuid>` sort key — but its id is only known at runtime (the
 * categories endpoint returns it), so neither can be a static constant.
 *
 * Shared by My Tasks and the project list, which build the same payload.
 */
export const buildCategoryCustomFieldFilter = (
  tableFilters: Record<string, unknown>,
  categoryFieldId: string | null,
): { customFieldDefinitionId: string; ids: string[] }[] => {
  if (!categoryFieldId) {
    return []
  }
  const selected = tableFilters[TASK_CATEGORY_FILTER_FIELD]
  const ids = (Array.isArray(selected) ? selected : [selected]).filter(
    (id): id is string => typeof id === 'string' && id !== '',
  )
  return ids.length ? [{ customFieldDefinitionId: categoryFieldId, ids }] : []
}

/** Category sorts through the server's custom-field path; other columns by name. */
export const toRequestOrderBy = (field: string, categoryFieldId: string | null): string =>
  field === TASK_CATEGORY_FILTER_FIELD && categoryFieldId ? customFieldKey(categoryFieldId) : field

/**
 * Where a task's unsent comment is parked, keyed per task so a half-written
 * comment on one doesn't follow you into the next. Mirrors Messages' `draftKey`.
 */
export const taskCommentDraftKey = (taskId: string): string => `task-comment-draft:${taskId}`

// Mirrors the server's assertProjectAdmin / assertCanChangeProjectStatus: the owner
// keeps the status actions even after being demoted from project admin.
export const getProjectPermissions = (
  project: Partial<Pick<ITaskProject, 'currentUserRole' | 'createdByUserId'>>,
  currentUserRole?: UserRole,
  currentUserId?: string,
): { isAdmin: boolean; canChangeStatus: boolean } => {
  const isAdmin = canManageProject(currentUserRole, project)
  const isOwner = !!currentUserId && project.createdByUserId === currentUserId
  return { isAdmin, canChangeStatus: isAdmin || isOwner }
}

/**
 * Whether a change to the composer's staged files is a person attaching one, and
 * so worth scrolling to.
 *
 * A saved draft coming back also takes the count from zero to N, and scrolling
 * for that drops the reader at the composer instead of at the comments. The two
 * are told apart by status, not by count: `stageFiles` appends entries as
 * `Uploading`, while `restore` replaces the list with entries already `Ready`.
 *
 * Keyed on status rather than on a render-ordering signal deliberately — an
 * earlier version compared the editor's remount key and silently skipped every
 * real add.
 */
export const isUserStagedAttachmentAdd = (
  previousCount: number,
  attachments: Array<{ status: AttachmentUploadStatus }>,
): boolean =>
  attachments.length > previousCount &&
  attachments.some((attachment) => attachment.status === AttachmentUploadStatus.Uploading)

/**
 * Who a just-posted comment suggests handing the task to: its first internal
 * mention that isn't Arc (Arc picks work up from the comment instead). Later
 * mentions never stand in, so a first mention that can't take this task, or
 * already has it, means no suggestion.
 */
export const findReassignCandidate = (
  mentionedUserIds: string[],
  internalUsers: IUser[],
  assignableUserIds: Set<string>,
  currentAssigneeId: string | null | undefined,
): IUser | null => {
  const candidate = mentionedUserIds
    .map((id) => internalUsers.find((user) => user.id === id))
    .find((user) => !!user && user.role !== UserRole.BOT_USER)
  if (!candidate || candidate.id === currentAssigneeId) return null
  return assignableUserIds.has(candidate.id) ? candidate : null
}

// For a menu whose trigger sits at the bottom of the screen (the panel's mobile
// action bar, the bulk bar), so it opens upward instead of covering the trigger.
export const ABOVE_TRIGGER_ANCHOR: PopoverOrigin = { vertical: 'top', horizontal: 'right' }
export const ABOVE_TRIGGER_TRANSFORM: PopoverOrigin = { vertical: 'bottom', horizontal: 'right' }
